import { useId, useState } from "react";
import { Combobox, FluentProvider, Option, webLightTheme } from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import styles from "./NoteWorkflows.module.css";

const memberPickerTheme = {
    ...webLightTheme,
    colorNeutralBackground1: "var(--color-input-bg, #fff)",
    colorNeutralForeground1: "var(--color-text-primary, #222)",
    colorNeutralForeground2: "var(--color-text-secondary, #666)",
    colorNeutralStroke1: "var(--color-border, #ddd)",
    colorNeutralBackground1Hover: "var(--color-bg-gray, #f3f3f3)",
    fontFamilyBase: "inherit"
};

interface MemberOption {
    id: string;
    label: string;
    disabled: boolean;
}

interface MemberPickerProps {
    label: string;
    value: string;
    options: MemberOption[];
    disabled: boolean;
    onChange: (id: string) => void;
}

export function MemberPicker({ label, value, options, disabled, onChange }: MemberPickerProps) {
    const { t } = useTranslation();
    const inputId = useId();
    const [query, setQuery] = useState<string | null>(null);
    const none = t("settingsPage.company.noteWorkflows.tasks.none", { defaultValue: "None" });
    const selectedLabel = options.find(option => option.id === value)?.label ?? none;
    const search = (query ?? "").trim().toLocaleLowerCase();
    const matches = options.filter(option => option.label.toLocaleLowerCase().includes(search));

    return (
        <FluentProvider theme={memberPickerTheme} className={styles.memberPickerField}>
            <label htmlFor={inputId}>{label}</label>
            <Combobox
                id={inputId}
                className={styles.memberPicker}
                input={{ className: styles.memberPickerInput, onFocus: event => event.currentTarget.select() }}
                listbox={{ className: styles.memberPickerList }}
                expandIcon={{ className: styles.memberPickerExpand }}
                inlinePopup
                value={query ?? (value ? selectedLabel : "")}
                selectedOptions={value ? [value] : []}
                disabled={disabled}
                placeholder={t("settingsPage.company.noteWorkflows.tasks.searchMembers", { defaultValue: "Search by name or email" })}
                onChange={event => setQuery(event.target.value)}
                onOpenChange={(_, data) => {
                    if (!data.open) setQuery(null);
                }}
                onOptionSelect={(_, data) => {
                    if (data.optionValue === undefined) return;
                    onChange(data.optionValue);
                    setQuery(null);
                }}
            >
                {!search && <Option value="" className={styles.memberPickerOption}>{none}</Option>}
                {matches.map(option => (
                    <Option key={option.id} value={option.id} text={option.label} disabled={option.disabled} className={styles.memberPickerOption}>
                        {option.label}
                    </Option>
                ))}
                {search && matches.length === 0 && (
                    <Option disabled className={styles.memberPickerOption}>
                        {t("settingsPage.company.noteWorkflows.tasks.noMatchingMembers", { defaultValue: "No matching members" })}
                    </Option>
                )}
            </Combobox>
        </FluentProvider>
    );
}
